import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import { useTranslate } from '@tolgee/react';
import { useAuthz } from '~/context/AuthzContext';
import {
  User2,
  Mail,
  Phone,
  MapPin,
  Download,
  Eye,
  Calendar,
  FileText,
  X,
  Users,
} from 'lucide-react';
import { clsx } from 'clsx';

interface InterestedBuyer {
  readonly id?: string;
  readonly firstname?: string;
  readonly lastname?: string;
  readonly gender?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly mobile?: string;
  readonly street?: string;
  readonly zip?: string;
  readonly city?: string;
  readonly status?: string;
  readonly hasDownloaded?: boolean;
  readonly hasOpened?: boolean;
  readonly hasBookedVisit?: boolean;
  readonly hasVisited?: boolean;
  readonly hasGivenOffer?: boolean;
  readonly lostInterestReason?: string;
  readonly createdAt?: string;
  readonly status?: string;
  readonly reason?: string;
  readonly propertyId?: string;
  readonly progress?: number;
}

interface Props {
  readonly title?: string;
  readonly data: InterestedBuyer[];
  readonly infoText?: string;
}

export function CockpitInterestedBuyersDetailWidget(props: Props) {
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const showDetails = atLeast('partner');
  if (!props.data?.length || props.data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t(props.title || 'cockpit_interested_buyers_detail_title')}
          </CardTitle>
          {props.infoText && (
            <p className="text-sm text-muted-foreground">
              {t(props.infoText as any) || props.infoText}
            </p>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={
              <Users className="w-8 h-8 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            }
            description={
              t('cockpit_interested_buyers_no_data') ||
              'Keine Interessenten vorhanden'
            }
            compact
          />
        </CardContent>
      </Card>
    );
  }

  // Sortiere: aktive Interessenten zuerst, dann nach Datum
  const sortedBuyers = [...props.data].sort((a, b) => {
    if (a.status === 'notinterested' && b.status !== 'notinterested') return 1;
    if (a.status !== 'notinterested' && b.status === 'notinterested') return -1;
    return (
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
    );
  });

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t(props.title || 'cockpit_interested_buyers_detail_title')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {sortedBuyers.length} Interessent
              {sortedBuyers.length !== 1 ? 'en' : ''}
            </span>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-primary" />
            </div>
          </div>
        </div>
        {props.infoText && (
          <p className="text-sm text-muted-foreground">
            {t(props.infoText as any) || props.infoText}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedBuyers.map((buyer, idx) => {
            const fullName =
              [
                buyer.firstname,
                buyer.lastname,
              ]
                .filter(Boolean)
                .join(' ') ||
              buyer.email ||
              'Unbekannt';
            const isLostInterest = buyer.status === 'notinterested';
            const statusCount = [
              buyer.hasDownloaded,
              buyer.hasOpened,
              buyer.hasBookedVisit,
              buyer.hasVisited,
              buyer.hasGivenOffer,
            ].filter(Boolean).length;

            return (
              <div
                key={buyer.id || idx}
                className={clsx(
                  'group hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:bg-gray-800/50 dark:hover:bg-gray-800/50 p-4 rounded-lg transition-colors duration-200 border',
                  isLostInterest
                    ? 'border-red-200 dark:border-red-900/50 bg-red-50/30 dark:bg-red-900/10'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:bg-gray-900',
                )}
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={clsx(
                      'h-10 w-10 rounded-lg flex items-center justify-center',
                      isLostInterest
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : statusCount >= 3
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : statusCount >= 1
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 dark:text-gray-400',
                    )}
                  >
                    {isLostInterest ? (
                      <X className="h-4 w-4" />
                    ) : (
                      <User2 className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {fullName}
                      </h3>
                      {isLostInterest && (
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full font-medium">
                          Abgesagt
                        </span>
                      )}
                    </div>
                    {!isLostInterest && statusCount > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {statusCount} Aktivität{statusCount !== 1 ? 'en' : ''}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                {showDetails ? (
                  <div className="space-y-2 text-sm mb-3">
                    {buyer.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                        <a
                          href={`mailto:${buyer.email}`}
                          className="text-gray-700 dark:text-gray-200 dark:text-gray-300 hover:text-primary-600 truncate text-xs"
                          data-track="immodossier cockpit buyers mailto"
                        >
                          {buyer.email}
                        </a>
                      </div>
                    )}
                    {(buyer.phone || buyer.mobile) && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                        <a
                          href={`tel:${buyer.mobile || buyer.phone}`}
                          className="text-gray-700 dark:text-gray-200 dark:text-gray-300 hover:text-primary-600 text-xs"
                          data-track="immodossier cockpit buyers tel"
                        >
                          {buyer.mobile || buyer.phone}
                        </a>
                      </div>
                    )}
                    {(buyer.street || buyer.zip || buyer.city) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                        <div className="text-xs text-gray-700 dark:text-gray-200 dark:text-gray-300">
                          {buyer.street && (
                            <span>
                              {buyer.street}
                              {(buyer.zip || buyer.city) && ', '}
                            </span>
                          )}
                          {(buyer.zip || buyer.city) && (
                            <span>
                              {buyer.zip} {buyer.city}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3"></div>
                )}

                {/* Activity Status */}
                <div className="space-y-2">
                  {buyer.hasDownloaded && (
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <Download className="h-3 w-3" />
                      <span>Heruntergeladen</span>
                    </div>
                  )}
                  {buyer.hasOpened && (
                    <div className="flex items-center gap-2 text-xs text-green-700">
                      <Eye className="h-3 w-3" />
                      <span>Geöffnet</span>
                    </div>
                  )}
                  {buyer.hasBookedVisit && (
                    <div className="flex items-center gap-2 text-xs text-purple-700">
                      <Calendar className="h-3 w-3" />
                      <span>Besichtigung gebucht</span>
                    </div>
                  )}
                  {buyer.hasVisited && (
                    <div className="flex items-center gap-2 text-xs text-indigo-700">
                      <Calendar className="h-3 w-3" />
                      <span>Besichtigt</span>
                    </div>
                  )}
                  {buyer.hasGivenOffer && (
                    <div className="flex items-center gap-2 text-xs text-amber-700">
                      <FileText className="h-3 w-3" />
                      <span>Angebot abgegeben</span>
                    </div>
                  )}
                  {isLostInterest && buyer.lostInterestReason && (
                    <div className="flex items-center gap-2 text-xs text-red-700">
                      <X className="h-3 w-3" />
                      <span>
                        {t(`rejection_reason_${buyer.lostInterestReason}`) ||
                          buyer.lostInterestReason}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
